import PropTypes from "prop-types";
import * as React from "react";
import { List } from "../themeV2";
import NavigationSection from "./NavigationSection";
import { ADMIN_SECTIONS, SECTION, UTILITY_SECTIONS } from "./config";
import useNavigationLinks from "./useNavigationLinks";

const ARIA_LABELS = {
  dashboard: "Shop navigation",
  mobile: "Main navigation",
  "account-menu": "Account navigation",
  "account-sidebar": "Account navigation",
};

/**
 * Classifies a section into a rendering group for divider placement.
 */
const getGroup = (sectionId) => {
  if (sectionId === SECTION.SHOP_DASHBOARD) return "shop";
  if (sectionId === SECTION.USER_ACCOUNT) return "account";
  if (ADMIN_SECTIONS.includes(sectionId)) return "admin";
  if (UTILITY_SECTIONS.includes(sectionId)) return "utility";
  return "other";
};

/**
 * Groups sections into ordered buckets, dropping empty groups.
 */
const GROUP_ORDER = ["shop", "account", "admin", "utility", "other"];

const groupSections = (sections) => {
  const grouped = sections.reduce((acc, section) => {
    const group = getGroup(section.id);
    return { ...acc, [group]: [...(acc[group] ?? []), section] };
  }, {});

  return GROUP_ORDER.filter((g) => grouped[g]?.length > 0).map((g) => ({
    key: g,
    sections: grouped[g],
  }));
};

NavigationRenderer.displayName = "NavigationRenderer";
NavigationRenderer.propTypes = {
  context: PropTypes.oneOf([
    "dashboard",
    "mobile",
    "account-menu",
    "account-sidebar",
  ]).isRequired,
  onLinkClick: PropTypes.func,
  className: PropTypes.string,
};

export default function NavigationRenderer({
  context,
  onLinkClick,
  className,
}) {
  const { sections } = useNavigationLinks(context);

  const groups = groupSections(sections);

  return (
    <nav
      aria-label={ARIA_LABELS[context] ?? "Navigation"}
      className={className}
    >
      <List.Root>
        {groups.map((group, groupIndex) => (
          <React.Fragment key={group.key}>
            {group.sections.map((section) => (
              <NavigationSection
                idx={groupIndex}
                key={section.id}
                id={section.id}
                label={context === "account-menu" ? null : section.label}
                links={section.links}
                collapsible={section.collapsible}
                onLinkClick={onLinkClick}
              />
            ))}
          </React.Fragment>
        ))}
      </List.Root>
    </nav>
  );
}
